<!--
  COD2 · Vendita su finanziamento — pacchetti valet venduti.

  SORGENTE: la tabella `minute`. ⚠️ Il nome inganna: `minute` NON riguarda i
  minuti, è il **pacchetto valet**, ed è legato all'AUTO (`minute.car`), non al
  cliente — un cliente con due auto ha due pacchetti.

  DUE FLAG DA NON CONFONDERE:
   - `accepted` = il cliente ha confermato il pacchetto. Finché è falso non è
     una vendita, quindi la pagina carica solo i confermati;
   - `esito`    = la vendita è stata fatturata. È lo stato che si chiude qui.

  L'id della rotta è quello del **partner**.

  ⚠️ COME NEL COD1, DUE AZIONI SEPARATE: «Salva» aggiorna gli importi e
  «Segna fatturate» chiude le righe scelte.

  Il totale è **1,22 × imponibile** (IVA), calcolato in automatico mentre si
  digita.
-->
<template>
  <div
    v-loading="loading"
    class="c2"
  >
    <div class="c2__head">
      <button
        type="button"
        class="c2__back"
        @click="goBack"
      >
        ‹
      </button>
      <div>
        <h1 class="c2__title">
          <span class="c2__code">COD2</span> Vendita su finanziamento
        </h1>
        <p class="c2__sub">
          {{ partnerName || 'Partner' }} — pacchetti valet venduti
        </p>
      </div>
    </div>

    <div class="c2__filters">
      <div class="field">
        <label>Mese</label>
        <div class="period">
          <button
            type="button"
            class="period__nav"
            @click="shiftMonth(-1)"
          >
            ‹
          </button>
          <span class="period__lbl">{{ periodLabel }}</span>
          <button
            type="button"
            class="period__nav"
            @click="shiftMonth(1)"
          >
            ›
          </button>
        </div>
      </div>
      <div class="c2__sp" />
      <el-input
        v-model="search"
        placeholder="Cliente o targa…"
        clearable
        style="width: 240px"
      />
    </div>

    <div class="kpis">
      <div class="kpi">
        <div class="kpi__lbl">
          Venduto nel mese
        </div>
        <div class="kpi__num">
          {{ euro(kpi.total) }}
        </div>
      </div>
      <div class="kpi kpi--attn">
        <div class="kpi__lbl">
          Da fatturare
        </div>
        <div class="kpi__num">
          {{ euro(kpi.toInvoice) }}
        </div>
      </div>
      <div class="kpi">
        <div class="kpi__lbl">
          Già fatturato
        </div>
        <div class="kpi__num">
          {{ euro(kpi.invoiced) }}
        </div>
      </div>
      <div class="kpi">
        <div class="kpi__lbl">
          Pacchetti
        </div>
        <div class="kpi__num">
          {{ minuti.length }}
        </div>
      </div>
    </div>

    <div class="board">
      <div class="toolbar">
        <div class="segs">
          <button
            v-for="s in SEGMENTS"
            :key="s.key"
            type="button"
            class="seg"
            :class="{ 'is-active': segment === s.key }"
            @click="setSegment(s.key)"
          >
            {{ s.label }}
            <span class="seg__c">{{ countOf(s.key) }}</span>
          </button>
        </div>
      </div>

      <el-table
        ref="tableRef"
        :data="filtered"
        style="width: 100%"
        row-key="id"
        :empty-text="segment === 'dafatt' && countOf('fatt')
          ? `Niente da fatturare: ${countOf('fatt')} ${countOf('fatt') === 1 ? 'pacchetto già fatturato' : 'pacchetti già fatturati'}, nella scheda Fatturate`
          : 'Nessun pacchetto valet venduto nel periodo selezionato'"
        @selection-change="onSelectionChange"
      >
        <el-table-column
          v-if="segment === 'dafatt'"
          type="selection"
          width="46"
        />

        <el-table-column
          label="Cliente"
          min-width="190"
        >
          <template #default="scope">
            <div class="cli__nm">
              {{ customerName(scope.row) }}
            </div>
            <div class="cli__sub">
              {{ fmtDate(scope.row.data_reg) }}
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="Auto"
          min-width="170"
        >
          <template #default="scope">
            <span v-if="scope.row.car">
              <!-- I campi dell'auto sono `marca`/`modello`/`targa`: con
                   brand/model/plate la colonna restava vuota. -->
              {{ [scope.row.car.marca, scope.row.car.modello].filter(Boolean).join(' ') }}
              <span class="cli__sub">{{ scope.row.car.targa }}</span>
            </span>
            <span
              v-else
              class="muted"
            >—</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Valet"
          width="120"
          align="right"
        >
          <template #default="scope">
            <span class="num">
              {{ scope.row.residual_valet ?? '—' }} / {{ scope.row.valet ?? '—' }}
            </span>
          </template>
        </el-table-column>

        <el-table-column
          label="Imponibile"
          width="150"
          align="right"
        >
          <template #default="scope">
            <el-input
              v-if="editing[scope.row.id]"
              v-model.number="draft[scope.row.id].imponibile"
              size="small"
              type="number"
              step="0.01"
            />
            <span
              v-else
              class="num"
            >{{ euro(scope.row.imponibile) }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Totale"
          width="150"
          align="right"
        >
          <template #default="scope">
            <!-- calcolato, non digitabile: è imponibile + IVA -->
            <span
              v-if="editing[scope.row.id]"
              class="num val num--calc"
            >{{ euro(withVat(draft[scope.row.id].imponibile)) }}</span>
            <span
              v-else
              class="num val"
            >{{ euro(scope.row.tot_fattura) }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Rif. fattura"
          width="150"
        >
          <template #default="scope">
            <el-input
              v-if="editing[scope.row.id]"
              v-model="draft[scope.row.id].rif_fattura"
              size="small"
            />
            <span
              v-else
              class="num"
            >{{ scope.row.rif_fattura || '—' }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Stato"
          width="150"
        >
          <template #default="scope">
            <StatusPill
              :kind="scope.row.esito ? 'ok' : 'attn'"
              :label="scope.row.esito ? 'Fatturato' : 'Da fatturare'"
            />
          </template>
        </el-table-column>

        <el-table-column
          label="Azioni"
          width="170"
          align="right"
          fixed="right"
        >
          <template #default="scope">
            <template v-if="editing[scope.row.id]">
              <el-button
                size="small"
                @click="cancelEdit(scope.row)"
              >
                Annulla
              </el-button>
              <el-button
                size="small"
                type="primary"
                :loading="saving === scope.row.id"
                @click="saveRow(scope.row)"
              >
                Salva
              </el-button>
            </template>
            <el-button
              v-else
              size="small"
              @click="startEdit(scope.row)"
            >
              Modifica
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div
        v-if="segment === 'dafatt'"
        class="bulk"
      >
        <span class="bulk__info">
          <b>{{ selected.length }}</b> selezionati · totale
          <b>{{ euro(selectedTotal) }}</b>
        </span>
        <span class="c2__sp" />
        <el-button
          type="success"
          :disabled="!selected.length"
          :loading="invoicing"
          @click="confirmInvoice"
        >
          Segna fatturate
        </el-button>
      </div>

      <div class="board__foot">
        <span>{{ filtered.length }} pacchetti</span>
        <span class="c2__sp" />
        <span class="board__tot">
          Totale mostrato <b>{{ euro(filteredTotal) }}</b>
        </span>
      </div>
    </div>

    <p class="c2__note">
      Il pacchetto valet è legato all'<b>auto</b>, non al cliente: un cliente con
      due auto compare con due pacchetti. Sono elencati solo quelli
      <b>confermati</b> dal cliente — gli altri non sono ancora una vendita.
    </p>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import contabilitaAPI from '../../../services/contabilitaAPI.js';
import { periodFromRoute, syncPeriodToUrl } from '../../../services/period.js';
import partnerAPI from '../../../services/partnerAPI.js';
import StatusPill from '../../../components/ui/StatusPill.vue';

const MONTHS = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

const SEGMENTS = [
  { key: 'dafatt', label: 'Da fatturare' },
  { key: 'fatt', label: 'Fatturate' },
];

// Aliquota applicata al pacchetto valet: regola già in uso, mantenuta.
const VAT = 1.22;

export default {
  name: 'Cod2Page',
  components: { StatusPill },

  setup() {
    const route = useRoute();
    const router = useRouter();
    // Il periodo arriva dalla URL (?y=&m=), non dal calendario: chi entra
    // dall'hub deve restare sul mese che stava guardando. Senza query valida
    // si ricade sul mese corrente.
    const periodo = periodFromRoute(route);
    const year = ref(periodo.year);
    const month = ref(periodo.month);

    const minuti = ref([]);
    const loading = ref(false);
    const saving = ref('');
    const invoicing = ref(false);
    const search = ref('');
    const segment = ref('dafatt');
    const selected = ref([]);
    const partnerName = ref('');
    const tableRef = ref(null);

    const editing = reactive({});
    const draft = reactive({});

    const periodLabel = computed(() => `${MONTHS[month.value - 1]} ${year.value}`);

    const customerName = (m) =>
      [m.car?.customer?.name, m.car?.customer?.surname].filter(Boolean).join(' ') || '—';

    const withVat = (imponibile) => Number(imponibile || 0) * VAT;

    const bySegment = (key) =>
      minuti.value.filter((m) => (key === 'fatt' ? m.esito : !m.esito));

    const countOf = (key) => bySegment(key).length;

    const filtered = computed(() => {
      const q = search.value.trim().toLowerCase();
      return bySegment(segment.value).filter((m) => {
        if (!q) return true;
        return `${customerName(m)} ${m.car?.plate || ''}`.toLowerCase().includes(q);
      });
    });

    const filteredTotal = computed(() =>
      filtered.value.reduce((s, m) => s + Number(m.tot_fattura || 0), 0)
    );

    const selectedTotal = computed(() =>
      selected.value.reduce((s, m) => s + Number(m.tot_fattura || 0), 0)
    );

    const kpi = computed(() => {
      let total = 0;
      let invoiced = 0;
      let toInvoice = 0;
      for (const m of minuti.value) {
        const v = Number(m.tot_fattura || 0);
        total += v;
        if (m.esito) invoiced += v;
        else toInvoice += v;
      }
      return { total, invoiced, toInvoice };
    });

    const load = async () => {
      loading.value = true;
      selected.value = [];
      for (const k of Object.keys(editing)) delete editing[k];
      try {
        const res = await contabilitaAPI.cod2Rows(
          route.params.id, year.value, month.value
        );
        minuti.value = res.minuti;
      } catch (e) {
        console.error('Caricamento COD2 fallito:', e);
        minuti.value = [];
      } finally {
        loading.value = false;
      }
    };

    const loadPartner = async () => {
      try {
        const r = await partnerAPI.getPartnerDetail(route.params.id);
        partnerName.value = r.data?.company_name || '';
      } catch (e) {
        console.warn('Nome partner non disponibile:', e?.message);
      }
    };

    // --- importi: il salvataggio NON tocca lo stato di fatturazione ---
    const startEdit = (row) => {
      draft[row.id] = {
        imponibile: Number(row.imponibile || 0),
        rif_fattura: row.rif_fattura || '',
      };
      editing[row.id] = true;
    };

    const cancelEdit = (row) => {
      delete editing[row.id];
      delete draft[row.id];
    };

    const saveRow = async (row) => {
      const d = draft[row.id];
      // `rif_fattura` è testo: «2026/0001» e «FT-0913» si
      // salvano come sono. Vuoto = nessun riferimento, cioè NULL: mandare
      // stringa vuota salverebbe un riferimento fatto di niente.
      const rif = String(d.rif_fattura ?? '').trim();
      saving.value = row.id;
      try {
        // Il messaggio d'errore lo mostra la pagina (sotto): senza
        // skipErrorToast l'interceptor ne aggiungerebbe un secondo generico.
        await contabilitaAPI.updateMinute(row.id, {
          imponibile: d.imponibile,
          tot_fattura: Number(withVat(d.imponibile).toFixed(2)),
          rif_fattura: rif || null,
        }, { skipErrorToast: true });
        ElMessage.success('Pacchetto aggiornato');
        cancelEdit(row);
        await load();
      } catch (e) {
        console.error('Salvataggio pacchetto fallito:', e);
        // 400 = regola del pacchetto violata (es. prezzo del venditore o fascia
        // di un pacchetto già confermato): il server dice quale, e va mostrato.
        const msg = e?.response?.data?.message;
        const detail = Array.isArray(msg) ? msg.join(', ') : msg;
        ElMessage.error({
          message: e?.response?.status === 400 && detail
            ? detail
            : e?.response?.status === 403
              ? 'Non hai i permessi per modificare questo pacchetto'
              : 'Salvataggio non riuscito',
          duration: 6000,
          showClose: true,
        });
      } finally {
        saving.value = '';
      }
    };

    // --- fatturazione: azione separata ---
    const confirmInvoice = async () => {
      try {
        await ElMessageBox.confirm(
          `${selected.value.length} pacchetti per ${euro(selectedTotal.value)} ` +
            'verranno segnati come fatturati.',
          'Confermi la fatturazione?',
          { confirmButtonText: 'Segna fatturate', cancelButtonText: 'Annulla' }
        );
      } catch {
        return;
      }

      invoicing.value = true;
      const res = await Promise.allSettled(
        selected.value.map((m) => contabilitaAPI.updateMinute(m.id, { esito: true }))
      );
      invoicing.value = false;

      const ko = res.filter((r) => r.status === 'rejected').length;
      if (ko) {
        ElMessage.error(
          `Fatturati ${res.length - ko} pacchetti su ${res.length}: ${ko} non riusciti.`
        );
      } else {
        ElMessage.success(`${res.length} pacchetti segnati come fatturati`);
      }
      await load();
    };

    const setSegment = (key) => {
      segment.value = key;
      selected.value = [];
      tableRef.value?.clearSelection?.();
    };

    const onSelectionChange = (rows) => { selected.value = rows; };

    const shiftMonth = (delta) => {
      let m = month.value + delta;
      let y = year.value;
      if (m < 1) { m = 12; y -= 1; }
      if (m > 12) { m = 1; y += 1; }
      month.value = m;
      year.value = y;
      // la URL segue il mese mostrato: un refresh non riporta a oggi
      syncPeriodToUrl(router, route, y, m);
      load();
    };

    function euro(n) {
      return `€ ${Number(n || 0).toFixed(2).replace('.', ',')}`;
    }

    const fmtDate = (d) =>
      d ? new Date(d).toLocaleDateString('it-IT') : '—';

    const goBack = () => router.push('/contabilita');

    onMounted(() => { load(); loadPartner(); });

    return {
      year, month, minuti, loading, saving, invoicing, search, segment,
      selected, partnerName, tableRef, editing, draft,
      filtered, filteredTotal, selectedTotal, kpi, periodLabel, SEGMENTS,
      countOf, setSegment, onSelectionChange, startEdit, cancelEdit, saveRow,
      confirmInvoice, shiftMonth, euro, fmtDate, customerName, withVat, goBack,
    };
  },
};
</script>

<style scoped>
.c2 { padding: 4px 0 32px; }
.c2__sp { flex: 1; }

.c2__head {
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin: 0 2px 18px;
}
.c2__back {
  width: 36px; height: 36px; flex: none; border-radius: 9px;
  border: 1px solid var(--border-color); background: var(--bg-primary);
  display: grid; place-items: center; cursor: pointer;
  font-size: 16px; color: var(--text-secondary); font-family: inherit;
}
.c2__back:hover { border-color: var(--brand-violet); }
.c2__title {
  margin: 0; font-size: 21px; display: flex; align-items: center; gap: 10px;
  color: var(--text-primary);
}
.c2__code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px; font-weight: 700; color: #fff; background: var(--state-ok);
  padding: 3px 8px; border-radius: 6px;
}
.c2__sub { margin: 3px 0 0; font-size: 13px; color: var(--text-tertiary); }

.c2__filters {
  display: flex; gap: 10px; flex-wrap: wrap; align-items: center; margin-bottom: 14px;
}
.field {
  display: flex; align-items: center; gap: 8px; padding: 5px 11px;
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: 10px;
}
.field label {
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--text-tertiary); font-weight: 700;
}
.period { display: flex; align-items: center; gap: 2px; }
.period__nav {
  width: 30px; height: 30px; border: 0; background: transparent;
  border-radius: 7px; cursor: pointer; font-size: 15px;
  color: var(--text-secondary); font-family: inherit;
}
.period__nav:hover { background: var(--bg-tertiary); }
.period__lbl {
  padding: 0 8px; font-weight: 700; font-variant-numeric: tabular-nums;
  min-width: 118px; text-align: center; color: var(--text-primary);
}

.kpis {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 16px;
}
@media (max-width: 820px) { .kpis { grid-template-columns: repeat(2, 1fr); } }
.kpi {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg); padding: 13px 15px;
}
.kpi__lbl {
  font-size: 11.5px; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--text-tertiary); font-weight: 600;
}
.kpi__num {
  font-size: 22px; font-weight: 750; letter-spacing: -0.02em; margin-top: 4px;
  font-variant-numeric: tabular-nums; color: var(--text-primary);
}
.kpi--attn .kpi__num { color: var(--state-attn); }

.board {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg); overflow: hidden;
}
.toolbar {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 12px 14px; border-bottom: 1px solid var(--border-color);
}
.segs { display: flex; gap: 5px; }
.seg {
  border: 1px solid transparent; background: transparent; cursor: pointer;
  color: var(--text-secondary); padding: 7px 12px; border-radius: 8px;
  font-size: 13px; font-weight: 600; font-family: inherit;
  display: inline-flex; align-items: center; gap: 8px;
}
.seg:hover { background: var(--bg-tertiary); }
.seg.is-active {
  background: var(--svc-fleet-bg); color: var(--brand-violet);
  border-color: var(--brand-violet);
}
.seg__c {
  font-size: 11.5px; font-weight: 700; border-radius: 20px; padding: 1px 8px;
  background: var(--state-muted-bg); color: var(--state-muted);
}
.seg.is-active .seg__c { background: var(--brand-violet); color: #fff; }

.bulk {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 12px 16px; border-top: 1px solid var(--border-color);
  background: var(--bg-secondary);
}
.bulk__info { font-size: 12.5px; color: var(--text-secondary); }
.bulk__info b { font-variant-numeric: tabular-nums; color: var(--text-primary); }

.cli__nm { font-weight: 600; color: var(--text-primary); }
.cli__sub { color: var(--text-tertiary); font-size: 11.5px; }
.num { font-variant-numeric: tabular-nums; }
.num--calc { color: var(--text-tertiary); }
.val { font-weight: 700; color: var(--text-primary); }
.muted { color: var(--text-tertiary); }

.board__foot {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 12px 14px; border-top: 1px solid var(--border-color);
  font-size: 12.5px; color: var(--text-tertiary);
}
.board__tot { color: var(--text-secondary); }
.board__tot b {
  color: var(--text-primary); font-variant-numeric: tabular-nums; font-size: 14px;
}

.c2__note {
  margin: 12px 0 0; font-size: 12px; color: var(--text-tertiary); line-height: 1.5;
}

.c2 :deep(.el-table th.el-table__cell) {
  background: var(--bg-tertiary) !important;
  color: var(--text-tertiary) !important;
  font-size: 11px; font-weight: 700; letter-spacing: 0.05em;
  padding: 10px 12px; border-bottom: 1px solid var(--border-color);
}
</style>
